/**
 * Pure lane geometry/grouping helpers, extracted from LaneMap.js so pages other
 * than the lane map (e.g. the station builder) can draw lane polylines without
 * pulling in the lane map component. No React, no api client — everything here
 * is a pure function of its arguments.
 *
 * `LaneMap.js` re-exports all of these, so its existing test suite (which
 * imports them from `./LaneMap`) keeps working unchanged.
 */

// Distinct hues so adjacent pairs out of one city stay tellable apart.
export const LANE_COLORS = [
  '#4285F4', '#EA4335', '#34A853', '#FBBC04', '#FF6D01',
  '#46BDC6', '#7B61FF', '#E91E63', '#00BCD4', '#8BC34A',
  '#FF5722', '#3F51B5', '#009688', '#FFC107', '#795548',
  '#607D8B', '#9C27B0', '#CDDC39', '#F44336', '#2196F3',
];

/**
 * Stable colour per city-PAIR key, independent of fetch/array order.
 *
 * The lanes query key includes `zoom` (server-side geometry simplification),
 * so a zoom change produces a fresh query and a fresh routes array whose
 * order (and truncation set) is not guaranteed stable. Panning alone does not
 * trigger this — `onMapIdle` only calls `setZoom(map.getZoom())`, so a pan
 * that doesn't also change the zoom level never refetches. Deriving the
 * colour from a hash of the pair key itself — rather than from insertion
 * order into a colour map — means a pair keeps the same colour across
 * refetches. Do not "simplify" this back to array-position-based assignment.
 *
 * The pair key is built from the two city names (see `cityPairKey` below),
 * which is even more stable than the old per-route id: it doesn't depend on
 * which specific route happened to be returned first, only on the two city
 * names, so it's identical across every refetch by construction.
 */
export const laneColorFor = (pairKey) => {
  const id = String(pairKey);
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % LANE_COLORS.length;
  return LANE_COLORS[index];
};

/**
 * The reverse lane's identity colour, in `LanePolylineEditor`.
 *
 * NOT a `laneColorFor` hash. The reverse lane is not "some other lane" that
 * happens to need a colour — it is always THE specific other direction of
 * this exact corridor, so it gets one fixed, deliberate colour rather than
 * whatever the hash lands on (which could collide with the primary lane's
 * own highlight colour, or with the candidate pink `EditorMap` already uses).
 * Exported once and imported by both `EditorMap.js` (the line on the map)
 * and `ReviewPanel.js` (the label naming that lane) so the two can never
 * drift apart — see the header comment on `EditorMap.js`'s reverse-lane
 * rendering for why that link matters operationally.
 */
export const REVERSE_LANE_COLOR = '#673AB7';

/**
 * GeoJSON LineString/MultiLineString -> Google Maps path (an array of
 * {lat, lng} points). A LineString's `coordinates` is already a flat array of
 * positions; a MultiLineString nests one level deeper (an array of lines,
 * each an array of positions) — the natural shape for a route assembled from
 * segments, or anything through ST_Union/ST_Collect — so it needs one
 * `.flat()` to reach the same position-array shape before the
 * [lng, lat] -> {lat, lng} transpose, mirroring how geoJsonToPolygonPaths
 * handles MultiPolygon below. Without this branch, a MultiLineString's
 * `coordinates` elements are themselves arrays, so `[lng, lat]` destructures
 * to arrays and each path entry silently becomes `{lat: Array, lng: Array}`
 * — Google Maps draws nothing and logs nothing.
 */
export const geoJsonToPath = (geoJsonString, routeId) => {
  if (!geoJsonString) return [];
  try {
    const parsed = JSON.parse(geoJsonString);
    const positions =
      parsed.type === 'MultiLineString' ? parsed.coordinates.flat() : parsed.coordinates || [];
    return positions.map(([lng, lat]) => ({ lat, lng }));
  } catch (e) {
    console.error(`geoJsonToPath: failed to parse polylineGeoJson for route ${routeId}`, e);
    return [];
  }
};

/**
 * GeoJSON Polygon/MultiPolygon -> Google Maps paths (arrays of {lat, lng}
 * rings). A Polygon's `coordinates` is already an array of rings; a
 * MultiPolygon nests one level deeper (an array of polygons, each an array of
 * rings), so it needs one `.flat()` to reach the same ring-array shape before
 * the [lng, lat] -> {lat, lng} transpose. `geojson` is `null` for an unknown
 * route (a legitimate "render nothing" response) and this returns `[]` for
 * that case and for anything unparseable, rather than throwing.
 */
export const geoJsonToPolygonPaths = (geoJsonString) => {
  if (!geoJsonString) return [];
  try {
    const parsed = JSON.parse(geoJsonString);
    const rings = parsed.type === 'MultiPolygon' ? parsed.coordinates.flat() : parsed.coordinates || [];
    return rings.map((ring) => ring.map(([lng, lat]) => ({ lat, lng })));
  } catch (e) {
    console.error('geoJsonToPolygonPaths: failed to parse buffer geojson', e);
    return [];
  }
};

/**
 * Unordered pair key from two city names — "Austin"/"Houston" and
 * "Houston"/"Austin" both produce the same key, which is exactly what lets
 * the sidebar collapse every route between two cities (in both directions)
 * into one row.
 */
export const cityPairKey = (cityNameA, cityNameB) => [cityNameA, cityNameB].sort().join('||');

/**
 * Groups routes (the "lanes" the API returns) into unordered city pairs.
 * Each pair carries every route between its two cities in either direction,
 * NON-OPERATIONAL ROUTES ARE DROPPED HERE, before grouping. They were briefly
 * drawn as subdued polylines so junk data stayed visible, but the page owner
 * found them confusing and asked for them gone: staging's Austin set has 30
 * routes of which 4 are non-operational, including two Austin↔Houston entries
 * with a nonsense 3-minute duration. Consequences of filtering at the source,
 * all intentional:
 *
 *   - a pair whose every route is non-operational disappears from the list
 *     entirely (Austin↔Fredericksburg, 2 routes, both non-operational), rather
 *     than showing as a row that draws nothing;
 *   - `routes` and the operational subset are now always the same set, so
 *     there is no "N non-op" badge left to show.
 *
 * If these come back, restore the distinction here rather than reintroducing a
 * second styling path at the render site.
 */
export const buildCityPairs = (routes) => {
  // `window.Map` is the built-in constructor, same object as the bare `Map`
  // global — this module no longer imports `ui/core`'s `Map` component, so the
  // shadowing this spelling guarded against is gone and the two are now
  // identical. Left as-is rather than churned back: `buildCityPairs` is pinned by
  // an "8 routes in, 6 kept" test, and a no-op edit to it buys nothing.
  const byKey = new window.Map();
  routes.forEach((route) => {
    if (!route.isOperational) return;
    const key = cityPairKey(route.originCityName, route.destinationCityName);
    if (!byKey.has(key)) {
      const [cityA, cityB] = [route.originCityName, route.destinationCityName].sort();
      byKey.set(key, { key, cityA, cityB, label: `${cityA} ↔ ${cityB}`, routes: [] });
    }
    byKey.get(key).routes.push(route);
  });

  // `operationalRoutes` is kept as a distinct name even though it is now
  // identical to `routes` — every consumer that means "routes that draw a
  // corridor band and get fetched for stations" reads it, so restoring
  // non-operational routes later is a change here and nowhere else.
  return Array.from(byKey.values()).map((pair) => ({
    ...pair,
    operationalRoutes: pair.routes,
  }));
};
